import { PARTNER_TYPES } from 'constants/messages';
import { adhocContractualRateRequests } from '../constants/athenaRateRequest';
import moment from 'moment';
import { AddedCharge } from '../interfaces/IAthenaRateRequest';

const formatValueIfNullOrUndefined = (value: any) =>
  value === null || value === undefined ? '' : value;

const formatDate = (date: any) => {
  if (date) {
    return moment(date).format('YYYY-MM-DD');
  }
  return '';
};

const hasValue = (value: any) =>
  typeof value === 'string' ? value.trim() !== '' : !!value;

// At any point only one of the fields carries a value. Precedence: Remarks (HP),
// then ServiceLevel (ASML), then ServiceLoadType (APPLE, Import only). Mirrors the
// backend adhoc-rate resolution. Remarks: the distinct values of ALL bookings are
// concatenated with ','. ServiceLevel / ServiceLoadType: first booking that has one.
const resolveExternalIdentifier = (
  bookings: any[],
  includeServiceLoadType: boolean,
): string | null => {
  const list = (bookings || []).filter(Boolean);

  const remarks = list
    .map((b: any) => b?.remarks)
    .filter(hasValue)
    .map((v: any) => (typeof v === 'string' ? v.trim() : v));
  if (remarks.length) {
    return Array.from(new Set(remarks)).join(',');
  }

  // ASML: ServiceLevel from the first booking that has one. The payload carries size and
  // type separately, so the value is passed through as-is (no Size-Type enrichment).
  const serviceLevel = list.find((b: any) => hasValue(b?.serviceLevel))
    ?.serviceLevel;
  if (hasValue(serviceLevel)) {
    return serviceLevel;
  }

  if (includeServiceLoadType) {
    const serviceLoadType = list.find((b: any) => hasValue(b?.serviceLoadType))
      ?.serviceLoadType;
    if (hasValue(serviceLoadType)) {
      return serviceLoadType;
    }
  }

  return null;
};

export const useCreateRequestObject = (
  documentType: string,
  currentFormData: any,
  exportStore: any,
  importStore: any,
  partners: any,
  additionalFields: any,
  selectedContainerSize: any,
  selectedContainerType: any,
  isAdditionalIncotermNeeded: boolean,
  selectedSplits: any[] = [],
  splitReferenceType?: string,
) => {
  const athenaRateRequest = { ...adhocContractualRateRequests };

  // type
  athenaRateRequest.documentType = documentType;
  athenaRateRequest.messageIdentifier = 'Origin';

  // charges
  athenaRateRequest.addedCharges[0].materialCode =
    currentFormData.materialCode?.toString();
  athenaRateRequest.addedCharges[0].materialDescription =
    currentFormData.materialDescription;
  athenaRateRequest.addedCharges[0].quantity =
    Number(currentFormData.quantity) || null;
  athenaRateRequest.addedCharges[0].unitOfMeasurement = currentFormData.uom;
  athenaRateRequest.addedCharges[0].payToPartyDetails =
    currentFormData.payToParty?.toString();
  athenaRateRequest.addedCharges[0].itemReference =
    currentFormData.itemReference || '';
  athenaRateRequest.addedCharges[0].itemCategory =
    currentFormData.itemCategory === 'REV' ? 'Revenue' : 'Cost';
  // Default for non-split charges (customers without an enabled charge-split rule).
  // Overridden per entry below when the charge is split.
  athenaRateRequest.addedCharges[0].referenceType = null;
  athenaRateRequest.addedCharges[0].referenceNumber = null;

  if (additionalFields?.length) {
    const modeOfTransport = additionalFields.find(
      (field: any) => field.key === 'modeOfTransport',
    );
    const numberOfDays = additionalFields.find((field: any) =>
      field.key.includes('Days'),
    );
    const palletType = additionalFields.find(
      (field: any) => field.key === 'pallettype',
    );

    athenaRateRequest.addedCharges[0].modeOfTransport =
      modeOfTransport?.value || '';
    athenaRateRequest.addedCharges[0].numberOfDays = numberOfDays?.value
      ? Number(numberOfDays.value)
      : null;
    athenaRateRequest.addedCharges[0].palletType = palletType?.value || '';
  } else {
    athenaRateRequest.addedCharges[0].modeOfTransport = '';
    athenaRateRequest.addedCharges[0].numberOfDays = null;
    athenaRateRequest.addedCharges[0].palletType = '';
  }

  // state data
  athenaRateRequest.containerSize = selectedContainerSize;
  athenaRateRequest.containerType = selectedContainerType;

  // bookings
  athenaRateRequest.cargoType =
    exportStore.exportDetailsBooking?.bookings[0]?.cargoType;
  athenaRateRequest.originSite =
    exportStore.exportDetailsBooking?.bookings[0]?.originSiteCode;

  const booking = exportStore?.exportDetailsBooking?.bookings?.filter(
    (x: any) => x?.originServiceSubType,
  )?.[0];

  // external identifier: ServiceLoadType applies to Import only
  athenaRateRequest.externalIdentifier = resolveExternalIdentifier(
    exportStore?.exportDetailsBooking?.bookings,
    documentType !== 'Export',
  );

  // export / import
  athenaRateRequest.incoTerm =
    documentType === 'Export'
      ? formatValueIfNullOrUndefined(exportStore.exportJobDetails.incoTerm)
      : formatValueIfNullOrUndefined(importStore.etsDetails.incoTerm);
  athenaRateRequest.originalDocumentNumber =
    documentType === 'Export'
      ? exportStore.exportJobDetails.originalDocumentNumber
      : importStore.etsDetails.documentNumber;
  athenaRateRequest.salesArea =
    documentType === 'Export'
      ? exportStore.exportJobDetails.salesArea
      : importStore.etsDetails.salesAreaRev;
  athenaRateRequest.departureDateEstimated =
    documentType === 'Export'
      ? formatDate(exportStore.exportJobDetails.voyages[0]?.etd)
      : formatDate(importStore.etsDetails.etd);
  athenaRateRequest.arrivalDateEstimated =
    documentType === 'Export'
      ? formatDate(exportStore.exportJobDetails.voyages[0]?.eta)
      : formatDate(importStore.etsDetails.eta);
  athenaRateRequest.specialCargoIndicator =
    documentType === 'Export'
      ? formatValueIfNullOrUndefined(
          exportStore.exportDetailsBooking?.bookings[0]?.specialCargoIndicator,
        )
      : formatValueIfNullOrUndefined(
          importStore.etsDetails.specialCargoIndicator,
        );
  athenaRateRequest.freightPpCollect =
    documentType === 'Export'
      ? exportStore.exportJobDetails?.prepaidCollectIndicator?.toUpperCase()
      : importStore.etsDetails?.shipment?.prepaidCollectIndicator?.toUpperCase();
  athenaRateRequest.carrierCode =
    documentType === 'Export'
      ? exportStore.exportDetailsCbl[0]?.carrierCode
      : importStore.etsDetails.cbls[0]?.carrierCode;
  athenaRateRequest.originCountry =
    documentType === 'Export'
      ? exportStore.exportJobDetails.placeOfReceiptCode?.substring(0, 2)
      : importStore.etsDetails.shipment.placeOfConsolidationCode?.substring(
          0,
          2,
        );
  athenaRateRequest.originCity =
    documentType === 'Export'
      ? exportStore.exportJobDetails.placeOfReceiptCode?.substring(2, 5)
      : importStore.etsDetails.shipment.placeOfConsolidationCode?.substring(
          2,
          5,
        );
  athenaRateRequest.originSite =
    documentType === 'Export'
      ? exportStore.exportJobDetails.placeOfReceiptCode?.substring(5, 8)
      : importStore.etsDetails.shipment.placeOfConsolidationCode?.substring(
          5,
          8,
        );
  athenaRateRequest.destinationCountry =
    documentType === 'Export'
      ? exportStore.exportJobDetails.placeOfDeliveryCode?.substring(0, 2)
      : importStore.etsDetails.shipment.placeOfDeliveryCode?.substring(0, 2);
  athenaRateRequest.destinationCity =
    documentType === 'Export'
      ? exportStore.exportJobDetails.placeOfDeliveryCode?.substring(2, 5)
      : importStore.etsDetails.shipment.placeOfDeliveryCode?.substring(2, 5);
  athenaRateRequest.destinationSite =
    documentType === 'Export'
      ? exportStore.exportJobDetails.placeOfDeliveryCode?.substring(5, 8)
      : importStore.etsDetails.shipment.placeOfDeliveryCode?.substring(5, 8);
  athenaRateRequest.placeOfReceipt =
    documentType === 'Export'
      ? exportStore.exportJobDetails.placeOfReceiptCode?.substring(2, 5)
      : importStore.etsDetails.documents[0].placeOfReceiptCode?.substring(2, 5);
  athenaRateRequest.placeOfReceiptSite =
    documentType === 'Export'
      ? exportStore.exportJobDetails.placeOfReceiptCode?.substring(5, 8)
      : importStore.etsDetails.documents[0].placeOfReceiptCode?.substring(5, 8);
  athenaRateRequest.placeOfDelivery =
    documentType === 'Export'
      ? exportStore.exportJobDetails.placeOfDeliveryCode?.substring(2, 5)
      : importStore.etsDetails.shipment.placeOfDeliveryCode?.substring(2, 5);
  athenaRateRequest.placeOfDeliverySite =
    documentType === 'Export'
      ? exportStore.exportJobDetails.placeOfDeliveryCode?.substring(5, 8)
      : importStore.etsDetails.shipment.placeOfDeliveryCode?.substring(5, 8);
  athenaRateRequest.portOfLoadingCountry =
    documentType === 'Export'
      ? exportStore.exportJobDetails.portOfLoadingCode?.substring(0, 2)
      : importStore.etsDetails.documents[0].portOfLoadingCode?.substring(0, 2);
  athenaRateRequest.portOfLoading =
    documentType === 'Export'
      ? exportStore.exportJobDetails.portOfLoadingCode?.substring(2, 5)
      : importStore.etsDetails.documents[0].portOfLoadingCode?.substring(2, 5);
  athenaRateRequest.portOfLoadingSite =
    documentType === 'Export'
      ? exportStore.exportJobDetails.portOfLoadingCode?.substring(5, 8)
      : importStore.etsDetails.documents[0].portOfLoadingCode?.substring(5, 8);
  athenaRateRequest.portOfDischarge =
    documentType === 'Export'
      ? exportStore.exportJobDetails.portOfDischargeCode?.substring(2, 5)
      : importStore.etsDetails.documents[0].portOfDischargeCode?.substring(
          2,
          5,
        );
  athenaRateRequest.portOfDischargeSite =
    documentType === 'Export'
      ? exportStore.exportJobDetails.portOfDischargeCode?.substring(5, 8)
      : importStore.etsDetails.documents[0].portOfDischargeCode?.substring(
          5,
          8,
        );
  athenaRateRequest.serviceTypeAtOrigin =
    documentType === 'Export'
      ? exportStore.exportJobDetails.serviceAtOrigin
      : importStore.etsDetails.documents[0]?.originService;
  athenaRateRequest.serviceTypeAtDestination =
    documentType === 'Export'
      ? exportStore.exportJobDetails.serviceAtDestination
      : importStore.etsDetails.documents[0]?.destinationService;
  athenaRateRequest.originServiceSubType =
    documentType === 'Export'
      ? (booking?.originServiceSubType || null)
      : null;
  athenaRateRequest.primaryTransportMode =
    documentType === 'Export'
      ? exportStore.exportJobDetails?.transportationMode
      : importStore.etsDetails?.transportationMode;
  athenaRateRequest.businessCategory =
    documentType === 'Export'
      ? exportStore.exportJobDetails?.businessCategory
      : importStore.etsDetails?.businessCategory;
  athenaRateRequest.shipmentTypeAtOrigin =
    documentType === 'Export'
      ? exportStore.exportJobDetails?.equipments?.[0]?.shipmentTypeAtOrigin
      : importStore.etsDetails?.equipmentTransport?.shipmentTypeAtOrigin;
  athenaRateRequest.shipmentTypeAtDestination =
    documentType === 'Export'
      ? exportStore.exportJobDetails?.equipments?.[0]?.shipmentTypeAtDestination
      : importStore.etsDetails?.equipmentTransport?.shipmentTypeAtDestination;
  athenaRateRequest.portOfDischargeCountry =
    documentType === 'Export'
      ? exportStore.exportJobDetails.portOfDischargeCode?.substring(0, 2)
      : importStore.etsDetails.documents[0].portOfDischargeCode?.substring(
          0,
          2,
        );
  athenaRateRequest.addedCharges[0].totalVolume =
    documentType === 'Export'
      ? exportStore.exportDetailsBooking.totalVolume
        ? Number(exportStore.exportDetailsBooking?.totalVolume)
        : null
      : importStore.etsDetails?.equipmentTransport?.volume
        ? Number(importStore.etsDetails?.equipmentTransport?.volume)
        : null;

  athenaRateRequest.transportContractNumber =
    documentType === 'Export'
      ? exportStore.exportDetailsCbl[0]?.transportContractNumber || null
      : importStore.etsDetails.cbls[0]?.transportContractNumber || null;

  if (documentType !== 'Export') {
    athenaRateRequest.carrierPlaceOfDeliveryCountry =
      importStore.etsDetails.carrierPlaceOfDeliveryCode?.substring(0, 2) || '';
    athenaRateRequest.carrierPlaceOfDeliveryCity =
      importStore.etsDetails.carrierPlaceOfDeliveryCode?.substring(2, 5) || '';
    athenaRateRequest.carrierPlaceOfDeliverySite =
      importStore.etsDetails.carrierPlaceOfDeliveryCode?.substring(5, 8) || '';
    athenaRateRequest.addedCharges[0].totalWeight = importStore.etsDetails
      ?.equipmentTransport?.weight
      ? Number(importStore.etsDetails?.equipmentTransport?.weight)
      : null;
    athenaRateRequest.emptyContainerReturnedCountry = importStore.etsDetails?.equipmentTransport?.emptyContainerReturnedCode?.substring(0, 2) || '';
    athenaRateRequest.emptyContainerReturnedCity = importStore.etsDetails?.equipmentTransport?.emptyContainerReturnedCode?.substring(2, 5) || '';
    athenaRateRequest.emptyContainerReturnedSite = importStore.etsDetails?.equipmentTransport?.emptyContainerReturnedCode?.substring(5, 8) || '';
  }

  // partners
  const result = partners.reduce((acc: any, partner: any) => {
    if (
      partner.partnerType === PARTNER_TYPES.SHIPPER ||
      partner.partnerType === PARTNER_TYPES.CONSIGNEE ||
      partner.partnerType === PARTNER_TYPES.INVOICEE
    ) {
      acc[partner.partnerType?.toLowerCase()] = partner.factCode;
    }
    return acc;
  }, {});
  athenaRateRequest.shipper = result.shipper ? result.shipper : '';
  athenaRateRequest.consignee = result.consignee ? result.consignee : '';
  athenaRateRequest.invoicee = result.invoicee ? result.invoicee : '';

  // contractual party details come from the Export / Import job details API
  athenaRateRequest.contractualParty =
    documentType === 'Export'
      ? formatValueIfNullOrUndefined(exportStore.exportJobDetails.contractualParty)
      : formatValueIfNullOrUndefined(importStore.etsDetails.contractualParty);
  athenaRateRequest.contractualPartyRole =
    documentType === 'Export'
      ? formatValueIfNullOrUndefined(
          exportStore.exportJobDetails.contractualPartyRole,
        )
      : formatValueIfNullOrUndefined(
          importStore.etsDetails.contractualPartyRole,
        );

  const getAdditionalIncoterm = (type: string, eStore: any, iStore: any) => {
    const equipment = type === 'Export' ? eStore.exportJobDetails.equipments?.[0] : iStore.etsDetails?.equipmentTransport;
    const incoTerm = type === 'Export' ? eStore.exportJobDetails.incoTerm : iStore.etsDetails.incoTerm;
    const primaryIncoterm = equipment?.primaryIncoterm === '-' ? null : equipment?.primaryIncoterm;
    const secondaryIncoterm = equipment?.secondaryIncoterm === '-' ? null : equipment?.secondaryIncoterm;
    let additionalIncoterm = null;
    if (primaryIncoterm && primaryIncoterm.trim() !== '' && secondaryIncoterm && secondaryIncoterm.trim() !== '') {
      additionalIncoterm = [primaryIncoterm, secondaryIncoterm].join('-');
    } else if (primaryIncoterm && primaryIncoterm.trim() !== '' && (!secondaryIncoterm || secondaryIncoterm.trim() === '')) {
      additionalIncoterm = primaryIncoterm;
    }
    if (additionalIncoterm && additionalIncoterm.trim() !== '') {
      if (incoTerm && incoTerm.trim() !== '') {
        return [incoTerm, additionalIncoterm].filter(s => s && s.trim() !== '').join('-');
      } else {
        return additionalIncoterm;
      }
    }
    return incoTerm || '';
  };

  if (isAdditionalIncotermNeeded) {
    athenaRateRequest.incoTerm = getAdditionalIncoterm(documentType, exportStore, importStore);
  }

  // Charge-split mode: fan the single fully-populated charge out into one entry per selected
  // split entity (SBK / FCR / ...). Each entry carries the entity's derived quantity, the split
  // referenceType and its referenceNumber (booking number). The response echoes referenceNumber
  // per charge, so response entries map back to the selected entity regardless of order.
  // itemReference is left untouched. totalVolume/totalWeight on the base entry are copied to each.
  if (selectedSplits?.length) {
    const baseCharge = athenaRateRequest.addedCharges[0];
    // SBK x lane split: a selected SBK carries its lane ids, and one entry is sent per SBK x lane.
    athenaRateRequest.addedCharges = selectedSplits.reduce(
      (charges: AddedCharge[], split: any) =>
        charges.concat(
          (split.lanes?.length ? split.lanes : [null]).map((laneId: string | null) => ({
            ...baseCharge,
            quantity: Number(split.quantity) || null,
            referenceType: splitReferenceType || null,
            referenceNumber: split.number ?? null,
            ...(laneId != null && { laneId }),
            ...(split.pickupLocation != null && { pickupLocation: split.pickupLocation }),
            ...(split.incoTerm != null && { incoTerm: split.incoTerm }),
          })),
        ),
      [],
    );
  }

  return { adhocContractualRateRequests: athenaRateRequest };
};
